import { createTheme, PaletteMode } from '@mui/material/styles';

// ContentSignal brand colors - deep purple/green gradient scheme
const lightPalette = {
  primary: {
    main: '#0EA5E9', // Sky Blue
    light: '#38BDF8',
    dark: '#0284C7',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#8B5CF6', // Soft Violet
    light: '#A78BFA',
    dark: '#7C3AED',
    contrastText: '#ffffff',
  },
  background: {
    default: '#FDFBF7', // Cream/Off-white for minimalist feel
    paper: '#ffffff',
    // Additional background colors
    section: '#F3F4F6', // Light gray
    accent: '#E0F2FE', // Light sky accent
  },
  text: {
    primary: '#1F2937', // Dark gray for excellent readability
    secondary: '#6B7280', // Medium gray for supporting text
  },
  grey: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
  success: {
    main: '#10B981',
    light: '#34D399',
    dark: '#059669',
  },
  warning: {
    main: '#F59E0B',
    light: '#FBBF24',
    dark: '#D97706',
  },
  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
  },
  info: {
    main: '#3B82F6',
    light: '#60A5FA',
    dark: '#2563EB',
  },
};

// Dark theme palette
const darkPalette = {
  primary: {
    main: '#38BDF8', // Bright Sky Blue for dark mode
    light: '#7DD3FC',
    dark: '#0EA5E9',
    contrastText: '#0F172A',
  },
  secondary: {
    main: '#A78BFA', // Bright Violet
    light: '#C4B5FD',
    dark: '#8B5CF6',
    contrastText: '#0F172A',
  },
  background: {
    default: '#0F172A', // Deep Slate
    paper: '#1E293B', // Slate 800
    section: '#1E293B',
    accent: '#1E3A8A', // Dark blue accent
  },
  text: {
    primary: '#F1F5F9', // Light gray for excellent readability on dark
    secondary: '#94A3B8', // Medium gray for supporting text
  },
  grey: {
    50: '#0F172A',
    100: '#1E293B',
    200: '#334155',
    300: '#475569',
    400: '#64748B',
    500: '#94A3B8',
    600: '#CBD5E1',
    700: '#E2E8F0',
    800: '#F1F5F9',
    900: '#F8FAFC',
  },
  success: {
    main: '#22C55E',
    light: '#4ADE80',
    dark: '#16A34A',
  },
  warning: {
    main: '#F59E0B',
    light: '#FBBF24',
    dark: '#D97706',
  },
  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
  },
  info: {
    main: '#3B82F6',
    light: '#60A5FA',
    dark: '#2563EB',
  },
};

// Custom breakpoints for responsive design
const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
  },
};

export const createAppTheme = (mode: PaletteMode) => {
  const palette = mode === 'light' ? lightPalette : darkPalette;

  return createTheme({
    palette: {
      mode,
      ...palette,
    },
    breakpoints,
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      h1: {
        fontSize: '3.5rem',
        fontWeight: 800,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
        '@media (max-width:900px)': {
          fontSize: '2.75rem',
        },
        '@media (max-width:600px)': {
          fontSize: '2.25rem',
        },
      },
      h2: {
        fontSize: '2.75rem',
        fontWeight: 700,
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
        '@media (max-width:900px)': {
          fontSize: '2.25rem',
        },
        '@media (max-width:600px)': {
          fontSize: '1.875rem',
        },
      },
      h3: {
        fontSize: '2.25rem',
        fontWeight: 700,
        lineHeight: 1.4,
        '@media (max-width:600px)': {
          fontSize: '1.75rem',
        },
      },
      h4: {
        fontSize: '1.75rem',
        fontWeight: 600,
        lineHeight: 1.4,
        '@media (max-width:600px)': {
          fontSize: '1.5rem',
        },
      },
      h5: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.5,
        '@media (max-width:600px)': {
          fontSize: '1.25rem',
        },
      },
      h6: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      subtitle1: {
        fontSize: '1.125rem',
        fontWeight: 500,
        lineHeight: 1.6,
      },
      subtitle2: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.6,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
        color: palette.text.primary,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.6,
        color: palette.text.secondary,
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '1rem',
        letterSpacing: '0.01em',
      },
      caption: {
        fontSize: '0.75rem',
        lineHeight: 1.4,
        color: palette.text.secondary,
      },
      overline: {
        fontSize: '0.75rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        lineHeight: 1.4,
      },
    },
    spacing: 8,
    shape: {
      borderRadius: 12,
    },
    shadows: [
      'none',
      mode === 'light'
        ? '0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)'
        : '0px 1px 3px rgba(0, 0, 0, 0.3), 0px 1px 2px rgba(0, 0, 0, 0.4)',
      mode === 'light'
        ? '0px 3px 6px rgba(0, 0, 0, 0.16), 0px 3px 6px rgba(0, 0, 0, 0.23)'
        : '0px 3px 6px rgba(0, 0, 0, 0.4), 0px 3px 6px rgba(0, 0, 0, 0.5)',
      mode === 'light'
        ? '0px 10px 20px rgba(0, 0, 0, 0.19), 0px 6px 6px rgba(0, 0, 0, 0.23)'
        : '0px 10px 20px rgba(0, 0, 0, 0.5), 0px 6px 6px rgba(0, 0, 0, 0.6)',
      mode === 'light'
        ? '0px 14px 28px rgba(0, 0, 0, 0.25), 0px 10px 10px rgba(0, 0, 0, 0.22)'
        : '0px 14px 28px rgba(0, 0, 0, 0.6), 0px 10px 10px rgba(0, 0, 0, 0.7)',
      mode === 'light'
        ? '0px 19px 38px rgba(0, 0, 0, 0.30), 0px 15px 12px rgba(0, 0, 0, 0.22)'
        : '0px 19px 38px rgba(0, 0, 0, 0.7), 0px 15px 12px rgba(0, 0, 0, 0.8)',
      // Repeat the last shadow for remaining indices
      ...Array(19).fill(
        mode === 'light'
          ? '0px 19px 38px rgba(0, 0, 0, 0.30), 0px 15px 12px rgba(0, 0, 0, 0.22)'
          : '0px 19px 38px rgba(0, 0, 0, 0.7), 0px 15px 12px rgba(0, 0, 0, 0.8)'
      ),
    ],
    components: {
      // Button component overrides
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            padding: '12px 24px',
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: mode === 'light'
                ? '0px 4px 12px rgba(107, 70, 193, 0.25)'
                : '0px 4px 12px rgba(139, 92, 246, 0.4)',
            },
            '&:active': {
              boxShadow: mode === 'light'
                ? '0px 2px 6px rgba(107, 70, 193, 0.3)'
                : '0px 2px 6px rgba(139, 92, 246, 0.5)',
            },
          },
          sizeLarge: {
            padding: '16px 32px',
            fontSize: '1.125rem',
            borderRadius: 12,
          },
          sizeSmall: {
            padding: '8px 16px',
            fontSize: '0.875rem',
            borderRadius: 8,
          },
          contained: {
            '&:hover': {
              boxShadow: mode === 'light'
                ? '0px 6px 16px rgba(107, 70, 193, 0.3)'
                : '0px 6px 16px rgba(139, 92, 246, 0.5)',
            },
          },
          outlined: {
            borderWidth: '2px',
            '&:hover': {
              borderWidth: '2px',
            },
          },
        },
      },
      // Card component overrides
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: mode === 'light'
              ? '0px 2px 8px rgba(0, 0, 0, 0.08)'
              : '0px 2px 8px rgba(0, 0, 0, 0.3)',
            transition: 'box-shadow 0.3s ease-in-out, transform 0.2s ease-in-out',
            '&:hover': {
              boxShadow: mode === 'light'
                ? '0px 8px 24px rgba(107, 70, 193, 0.15)'
                : '0px 8px 24px rgba(139, 92, 246, 0.25)',
              transform: 'translateY(-2px)',
            },
          },
        },
      },
      // AppBar component overrides
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: palette.background.paper,
            color: palette.text.primary,
            boxShadow: mode === 'light'
              ? '0px 1px 3px rgba(0, 0, 0, 0.08)'
              : '0px 1px 3px rgba(0, 0, 0, 0.3)',
            borderBottom: `1px solid ${palette.grey[mode === 'light' ? 200 : 700]}`,
            borderRadius: 0, // Ensure squared corners
          },
        },
      },
      // Drawer component overrides
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRadius: 0, // Ensure squared corners for sidebar
            borderRight: `1px solid ${palette.grey[mode === 'light' ? 200 : 700]}`,
          },
        },
      },
      // Toolbar component overrides
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: '64px !important',
            '@media (min-width: 600px)': {
              minHeight: '72px !important',
            },
          },
        },
      },
      // Container component overrides
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingLeft: '16px',
            paddingRight: '16px',
            '@media (min-width: 600px)': {
              paddingLeft: '24px',
              paddingRight: '24px',
            },
          },
        },
      },
      // Paper component overrides
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
          elevation1: {
            boxShadow: mode === 'light'
              ? '0px 1px 3px rgba(0, 0, 0, 0.08)'
              : '0px 1px 3px rgba(0, 0, 0, 0.3)',
          },
        },
      },
      // TextField component overrides
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: palette.primary.main,
              },
            },
          },
        },
      },
      // Chip component overrides
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            fontWeight: 600,
          },
        },
      },
      // Typography component overrides
      MuiTypography: {
        styleOverrides: {
          gutterBottom: {
            marginBottom: '0.75em',
          },
        },
      },
      // Link component overrides
      MuiLink: {
        styleOverrides: {
          root: {
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
        },
      },
      // Divider component overrides
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: palette.grey[mode === 'light' ? 200 : 700],
          },
        },
      },
    },
  });
};

// Export default light theme for backward compatibility
export const theme = createAppTheme('light');

// TypeScript module augmentation for custom background colors
declare module '@mui/material/styles' {
  interface Palette {
    background: {
      default: string;
      paper: string;
      section: string;
      accent: string;
    };
  }

  interface PaletteOptions {
    background?: {
      default?: string;
      paper?: string;
      section?: string;
      accent?: string;
    };
  }
}

// Theme mode type
export type ThemeMode = 'light' | 'dark' | 'system';